import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { FormGroup } from '@angular/forms';
import { TwAforoViewModel } from '../aforos.types';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { CultivoViewModel } from '../../../../_campanias/cultivos/cultivo.types';
import { TwRecintoViewModel } from '../../../../_campanias/recintos/recinto.types';
import { TwTcVariedadCamViewModel } from '../../../especies/variedades/variedad.types';
import { MonthStringNumberToStringPipe, MonthStringToStringNumberPipe } from 'src/app/components/core/_infrastructure/classes/pipes';

@Component({
    selector: 'app-aforo-edit',
    templateUrl: './aforo-edit.component.html',
})
export class AforoEditComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() editMode = true;
    @Input() aforo: TwAforoViewModel;
    @Input() formGroup: FormGroup;
    @Input() comunidadesAutonomas: Tc[];
    @Input() provincias: Tc[];
    @Input() recintos: TwRecintoViewModel[];
    @Input() cultivos: CultivoViewModel[];
    @Input() controlsDisabled = false;

    @Output() comunidadAutonomaSelected = new EventEmitter<number>();
    @Output() provinciaSelected = new EventEmitter<number>();
    @Output() recintoSelected = new EventEmitter<number>();

    variedades: TwTcVariedadCamViewModel[];

    monthNames: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    descripcionVariedadCultivoSeleccionada: string = '(variedad del cultivo)';
    codigoSigpacCultivoSelected: string = '(código sigpac)';
    identificadorCultivoSelected: string = '(identificador)';
    descripcionAnyoSeleccionado: string = '(año)';
    numeroMesSeleccionado: string = '(mes)';
    descripcionMesSeleccionado: string;

    isCollapsedCabecera = false;

    constructor(
        private monthStringToStringNumberPipe: MonthStringToStringNumberPipe,
        private monthStringNumberToStringPipe: MonthStringNumberToStringPipe,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
        this.currentYear;
    }

    get currentYear(): number {
        return new Date().getFullYear();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.aforo?.currentValue) {
            this.aforo = { ...changes.aforo.currentValue };
            this.updateForm();
            this.convertMonthNumberToString();
        }

        if (changes?.provincias?.currentValue && this.aforo?.IdProvincia && this.form.untouched) {
            this.setIdProvincia(this.aforo?.IdProvincia || null);
        }

        if (changes?.recintos?.currentValue && this.aforo?.IdRecinto && this.form.untouched) {
            this.setIdRecinto(this.aforo?.IdRecinto || null);
        }

        if (changes?.cultivos?.currentValue && this.aforo?.IdCultivo && this.form.untouched) {
            this.setIdCultivo(this.aforo?.IdCultivo || null);
        }

        if (changes?.provincias?.currentValue || changes?.recintos?.currentValue || changes?.cultivos?.currentValue) {
            this.updateDescripcion();
        }
    }

    /**
     * This functions sets the values in the aforo (@Input) into the form.
     */
    private updateForm(): void {
        this.formGroup.setValue({
            Id: this.aforo?.Id || null,
            IdComunidadAutonoma: this.aforo?.IdComunidadAutonoma || null,
            IdProvincia: this.aforo?.IdProvincia || null,
            IdRecinto: this.aforo?.IdRecinto || null,
            IdCultivo: this.aforo?.IdCultivo || null,
            Anyo: this.aforo?.Anyo || null,
            Mes: this.aforo?.Mes || null,
            Descripcion: this.aforo?.Descripcion || null,
            KgsProduccionEstimada: this.aforo?.KgsProduccionEstimada || 0,
            KgsProduccionReal: this.aforo?.KgsProduccionReal || 0,
        });

        if (this.aforo?.IdComunidadAutonoma) {
            this.onSelectComunidadAutonoma(null);
        }

        this.form = this.formGroup; // componentBase uses this.form
    }

    onSelectComunidadAutonoma($event: any): void {
        this.form.patchValue({
            IdProvincia: null,
            IdRecinto: null,
            IdCultivo: null,
        });

        this.provincias = [];
        this.recintos = [];
        this.cultivos = [];

        if (!this.form.untouched) {
            this.descripcionVariedadCultivoSeleccionada = '(variedad del cultivo)';
            this.codigoSigpacCultivoSelected = '(código sigpac)';
            this.identificadorCultivoSelected = '(identificador)';
        }

        const idComunidadAutonoma = this.form.controls.IdComunidadAutonoma.value;

        this.comunidadAutonomaSelected.emit(idComunidadAutonoma);
    }

    onSelectProvincia($event: any): void {
        this.form.patchValue({
            IdRecinto: null,
            IdCultivo: null,
        });

        this.recintos = [];
        this.cultivos = [];

        if (!this.form.untouched) {
            this.descripcionVariedadCultivoSeleccionada = '(variedad del cultivo)';
            this.codigoSigpacCultivoSelected = '(código sigpac)';
            this.identificadorCultivoSelected = '(identificador)';
        }

        const idProvincia = this.form.controls.IdProvincia.value;

        this.provinciaSelected.emit(idProvincia);
    }

    private setIdProvincia(idProvincia: number | null): void {
        this.formGroup.patchValue({
            IdProvincia: idProvincia,
        });

        this.onSelectProvincia(idProvincia);
    }

    onSelectRecinto($event: any): void {
        this.form.patchValue({
            IdCultivo: null,
        });

        this.cultivos = [];

        if (!this.form.untouched) {
            this.descripcionVariedadCultivoSeleccionada = '(variedad del cultivo)';
            this.codigoSigpacCultivoSelected = '(código sigpac)';
            this.identificadorCultivoSelected = '(identificador)';
        }

        const idRecinto = this.form.controls.IdRecinto.value;

        this.recintoSelected.emit(idRecinto);
    }

    private setIdRecinto(idRecinto: number | null): void {
        this.formGroup.patchValue({
            IdRecinto: idRecinto,
        });

        this.onSelectRecinto(idRecinto);
    }

    private setIdCultivo(idCultivo: number | null): void {
        this.formGroup.patchValue({
            IdCultivo: idCultivo,
        });
    }

    onMonthSelect(event: any) {
        const selectedDate = event;

        if (selectedDate) {
            const monthAbbreviation = selectedDate.toLocaleString('default', { month: 'short' }).toUpperCase();

            if (monthAbbreviation === 'SEPT') {
                const monthFullName = this.monthNames.find(v => v.substring(0, 4).toUpperCase() === monthAbbreviation);
                this.descripcionMesSeleccionado = monthFullName!;

                this.form.patchValue({
                    Mes: monthFullName,
                });
            } else {
                const monthFullName = this.monthNames.find(v => v.substring(0, 3).toUpperCase() === monthAbbreviation);
                this.descripcionMesSeleccionado = monthFullName!;

                this.form.patchValue({
                    Mes: monthFullName,
                });
            }

            this.numeroMesSeleccionado = this.monthStringToStringNumberPipe.transform(this.descripcionMesSeleccionado)!;
        }
    }

    private convertMonthNumberToString(): void {
        let monthNumber = this.aforo.Descripcion.substring(this.aforo.Descripcion.length - 2);

        this.form.patchValue({
            Mes: this.monthStringNumberToStringPipe.transform(monthNumber),
        });

        this.numeroMesSeleccionado = monthNumber;
    }

    calcularDescripcionCompleta(): string {
        if (this.controlsDisabled) {
            this.form.patchValue({
                Descripcion: this.aforo?.Descripcion,
            });
            return this.aforo?.Descripcion || '';
        } else if (this.aforo?.Descripcion && this.form.untouched) {
            this.form.patchValue({
                Descripcion: this.aforo?.Descripcion,
            });
            return this.aforo?.Descripcion || '';
        } else if (
            this.aforo?.Descripcion &&
            !this.form.untouched &&
            this.form.value.IdComunidadAutonoma === this.aforo?.IdComunidadAutonoma &&
            this.form.value.IdProvincia === this.aforo?.IdProvincia &&
            this.form.value.IdRecinto === this.aforo?.IdRecinto &&
            this.form.value.IdCultivo === this.aforo?.IdCultivo &&
            this.form.value.Anyo === this.aforo?.Anyo &&
            this.form.value.Mes === this.aforo?.Mes
        ) {
            this.form.patchValue({
                Descripcion: this.aforo?.Descripcion,
            });
            return this.aforo?.Descripcion || '';
        } else {
            const descripcionCompleta = `${this.descripcionVariedadCultivoSeleccionada}-${this.codigoSigpacCultivoSelected}-${this.identificadorCultivoSelected}-${this.descripcionAnyoSeleccionado}${this.numeroMesSeleccionado}`;
            this.form.patchValue({
                Descripcion: descripcionCompleta,
            });
            return descripcionCompleta;
        }
    }

    onChangeCultivo(eventId: any): void {
        if (eventId) {
            const cultivoSelected = this.cultivos.find(c => c.Id === eventId);

            if (cultivoSelected) {
                this.descripcionVariedadCultivoSeleccionada = cultivoSelected.Variedad!;
                this.codigoSigpacCultivoSelected = cultivoSelected.CodigoSigPacRecinto!;
                this.identificadorCultivoSelected = cultivoSelected.Identificador;
            }
        } else {
            this.descripcionVariedadCultivoSeleccionada = '(variedad del cultivo)';
            this.codigoSigpacCultivoSelected = '(código sigpac)';
            this.identificadorCultivoSelected = '(identificador)';
        }
    }

    onChangeAnyo(): void {
        if (this.aforo?.Anyo && this.form.untouched && !this.form.value.Anyo) {
            this.descripcionAnyoSeleccionado = this.aforo?.Anyo.toString();
        } else if (this.form.value.Anyo) {
            this.descripcionAnyoSeleccionado = this.form.value.Anyo.toString();
        }
    }

    private updateDescripcion(): void {
        this.onChangeCultivo(this.form.value.IdCultivo);
        this.onChangeAnyo();
        const descripcionCompleta = this.calcularDescripcionCompleta();
        this.form.get('Descripcion')?.setValue(descripcionCompleta, { emitEvent: false });
    }

    get title(): string {
        return this.aforo ? (this.editMode ? 'Editar' : 'Detalle del') : 'Nuevo';
    }
}
